<script lang="ts" setup>
import { computed, toRefs } from "vue";
import { getUniqueId } from "@/shared/helper/uniqueId";
import Checkbox from "@/shared/components/Checkbox/Checkbox.vue";
import Tooltip from "@/shared/components/Tooltip/Tooltip.vue";

const model = defineModel<boolean>({ required: true });

const props = withDefaults(
  defineProps<{
    id?: string;
    disabled?: boolean;
    disabledInfo?: string;
    errors?: Array<string>;
  }>(),
  { errors: () => [], disabled: false },
);

const { errors } = toRefs(props);

// --- Errors ---
const hasErrors = computed(() => {
  return errors.value && errors.value.length > 0;
});

// --- Disabled info ---
// Rendered as persistent accessible text so it's discoverable without hover/focus
const disabledInfoId = getUniqueId("checkbox-disabled-info");
</script>

<template>
  <label
    class="checkbox-field"
    :data-checked="model"
    :data-disabled="disabled"
  >
    <div class="checkbox-field--wrapper">
      <div class="checkbox-field--input-wrapper">
        <Tooltip
          v-if="disabled && disabledInfo"
          :text="disabledInfo"
        >
          <Checkbox
            :id="id"
            v-model="model"
            :disabled="disabled"
            :described-by="disabledInfoId"
          />
        </Tooltip>
        <Checkbox
          v-else
          :id="id"
          v-model="model"
          :disabled="disabled"
        />
      </div>
      <span class="checkbox-field--label">
        <slot></slot>
      </span>
    </div>
    <span
      v-if="hasErrors"
      class="checkbox-field--error-message"
    >
      {{ errors[0] }}
    </span>
  </label>
  <span
    v-if="disabled && disabledInfo"
    :id="disabledInfoId"
    class="screen-reader-only"
  >
    {{ disabledInfo }}
  </span>
</template>

<style scoped>
@import "CheckboxField.css";
</style>
